import JsBarcode from "jsbarcode";
import { forwardRef, useRef, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, Share2, Printer, RefreshCw, Trash2,
  Download, Plus, X, CheckCircle, Circle, ExternalLink,
  Edit2, FileText, Check, Upload,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { ColorDot } from "@/components/ColorDot";
import { formatDate } from "@/utils/formatDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ALL_STATUSES = [
  "Received","Under Investigation","Waiting for Approval",
  "Repair in Progress","Repaired","Replacement Approved",
  "Replacement Dispatched","Completed","Warranty Rejected",
];

// Display names + subtitles for the Service Journey (shown on this page only)
const STATUS_DISPLAY = {
  "Received":               { name: "Received",               sub: "Service entry received",               emoji: "📋" },
  "Under Investigation":    { name: "Under Inspection",        sub: "Device being inspected",               emoji: "🔍" },
  "Waiting for Approval":   { name: "Waiting for Approval",    sub: "Awaiting customer or admin approval",  emoji: "⏳" },
  "Repair in Progress":     { name: "Repair in Progress",      sub: "Repair underway by technician",        emoji: "🔧" },
  "Repaired":               { name: "Repaired",                sub: "Repair complete",                      emoji: "✅" },
  "Replacement Approved":   { name: "Replacement Approved",    sub: "Replacement unit approved",            emoji: "🔄" },
  "Replacement Dispatched": { name: "Replacement Dispatched",  sub: "Replacement dispatched to customer",   emoji: "📦" },
  "Completed":              { name: "Completed",               sub: "Service successfully completed",       emoji: "🎉" },
  "Warranty Rejected":      { name: "Warranty Rejected",       sub: "Warranty claim not accepted",          emoji: "❌" },
};
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const YEARS = Array.from({ length: 9 }, (_, i) => String(2018 + i));
const EMPTY_CHARGER = { charger_model:"", voltage:"", ampere:"", body_type:"", serial_number:"", mfg_month:"", mfg_year:"", warranty_status:"" };

function normalizeChargers(t) {
  if (t.chargers && t.chargers.length > 0) return t.chargers;
  if (t.charger_model) return [{ charger_model:t.charger_model||"", voltage:t.voltage||"", ampere:t.ampere||"", body_type:t.body_type||"", serial_number:t.serial_number||"", mfg_month:t.mfg_month||"", mfg_year:t.mfg_year||"", warranty_status:t.warranty_status||"" }];
  return [{ ...EMPTY_CHARGER }];
}

// ─── Real Code-128 Barcode via JsBarcode ─────────────────────────────────────
const BarcodeStrip = forwardRef(({ ticketId }, fwdRef) => {
  const innerRef = useRef(null);

  const mergeRef = useCallback((node) => {
    innerRef.current = node;
    if (typeof fwdRef === "function") fwdRef(node);
    else if (fwdRef != null) fwdRef.current = node;
  }, [fwdRef]);

  useEffect(() => {
    const el = innerRef.current;
    if (!el || !ticketId) return;
    try {
      JsBarcode(el, ticketId, {
        format: "CODE128",
        width: 2,
        height: 60,
        displayValue: true,
        text: ticketId,
        fontSize: 11,
        margin: 8,
        background: "transparent",
        lineColor: "#111827",
        textMargin: 3,
        fontOptions: "bold",
      });
    } catch (e) { console.warn("JsBarcode:", e); }
  }, [ticketId]);

  return <svg ref={mergeRef} className="w-full max-w-full block" />;
});
BarcodeStrip.displayName = "BarcodeStrip";

// ─── Status Modal ─────────────────────────────────────────────────────────────
function StatusModal({ ticket, onUpdate, onClose }) {
  const [newStatus, setNewStatus] = useState(ticket.status);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const handleUpdate = async () => {
    if (!newStatus || newStatus === ticket.status) return;
    setLoading(true);
    try { await onUpdate(newStatus, notes); onClose(); }
    catch (err) { console.error("Status update error:", err); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-gray-900 mb-4" style={{fontFamily:"'Outfit',sans-serif"}}>Update Status</h3>
        <Select value={newStatus} onValueChange={setNewStatus}>
          <SelectTrigger className="border-gray-200 mb-3"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ALL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Add notes (optional)..." className="border-gray-200 mb-4 text-sm" rows={3} data-testid="status-notes" />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors">Cancel</button>
          <button onClick={handleUpdate} disabled={loading || newStatus === ticket.status} data-testid="update-status-btn"
            className="px-5 py-2 text-sm font-bold text-white bg-lime-600 hover:bg-lime-700 rounded-xl transition-colors disabled:opacity-50">
            {loading ? "Updating..." : "Update Status"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────
function InfoRow({ label, value, mono = false, full = false }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-0.5">{label}</p>
      <p className={`text-sm font-medium text-gray-900 leading-relaxed ${mono ? "font-mono" : ""}`}>
        {value || <span className="text-gray-300 font-normal">—</span>}
      </p>
    </div>
  );
}

function SectionCard({ title, badge, onEdit, editMode, onSave, onCancel, saving, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="px-6 py-3.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">{title}</h3>
          {badge}
        </div>
        {editMode ? (
          <div className="flex gap-1.5">
            <button onClick={onCancel} className="px-2.5 py-1 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
            <button onClick={onSave} disabled={saving} className="px-2.5 py-1 text-xs font-bold text-white bg-lime-600 rounded-lg hover:bg-lime-700 disabled:opacity-50 transition-colors">
              {saving ? "..." : "Save"}
            </button>
          </div>
        ) : onEdit ? (
          <button onClick={onEdit} className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Edit2 className="w-3 h-3" /> Edit
          </button>
        ) : null}
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function EditF({ label, value, onChange, type = "text", multiline = false, placeholder = "" }) {
  return (
    <div>
      <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5 block">{label}</Label>
      {multiline
        ? <Textarea value={value || ""} onChange={onChange} rows={2} placeholder={placeholder} className="text-sm border-gray-200 focus:ring-lime-500 focus:border-lime-500" />
        : <Input value={value || ""} onChange={onChange} type={type} placeholder={placeholder} className="text-sm border-gray-200 focus:ring-lime-500 focus:border-lime-500" />}
    </div>
  );
}

function SelF({ label, value, onChange, options }) {
  return (
    <div>
      <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5 block">{label}</Label>
      <Select value={value || "_blank_"} onValueChange={v => onChange(v === "_blank_" ? "" : v)}>
        <SelectTrigger className="border-gray-200 focus:ring-lime-500 text-sm"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="_blank_">— Select —</SelectItem>
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// ─── Charger segment definitions ─────────────────────────────────────────────
const CHARGER_SEGMENTS = {
  "Small charger":     { label: "Small Charger",    models: ["54.6V/6A","58.4V/6A","67.2V/6A","69.3V/6A","71.2V/6A","73V/6A","84V/5A"] },
  "Fast charger":      { label: "Fast Charger",     models: ["54.6V/10A","58.4V/10A","67.2V/10A","69.3V/10A","71.2V/10A","73V/10A","84V/10A"] },
  "E rickshaw charger":{ label: "Rickshaw Charger", models: ["54.6V/15A","58.4V/15A","67.2V/15A","69.3V/15A","71.2V/15A","73V/15A","54.6V/20A","58.4V/20A","67.2V/20A","69.3V/20A","71.2V/20A","73V/20A"] },
};
function parseChargerModel(s) {
  if (!s || !s.includes("/")) return { voltage:"", ampere:"" };
  const [v,a] = s.split("/"); return { voltage: v||"", ampere: a||"" };
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const barcodeRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Section-level edit states
  const [editCustomer, setEditCustomer] = useState(false);
  const [editPayment, setEditPayment] = useState(false);
  const [editCourier, setEditCourier] = useState(false);
  const [editCharger, setEditCharger] = useState(false);
  const [editAll, setEditAll] = useState(false);

  const handleEditAll = () => {
    setEditAll(true);
    setEditCustomer(true);
    setEditCharger(true);
    setEditPayment(true);
    setEditCourier(true);
  };

  const handleCancelAll = () => {
    setEditAll(false);
    setEditCustomer(false);
    setEditCharger(false);
    setEditPayment(false);
    setEditCourier(false);
    initForms(ticket);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const allData = {
        ...custForm,
        chargers: chargerForm,
        service_type: repairForm.service_type,
        received_date: repairForm.received_date,
        fault_description: repairForm.fault_description,
        repair_notes: repairForm.repair_notes,
        payment_total: payForm.payment_total ? parseFloat(payForm.payment_total) : null,
        payment_paid: payForm.payment_paid ? parseFloat(payForm.payment_paid) : null,
        payment_method: payForm.payment_method || null,
        payment_notes: payForm.payment_notes || null,
        courier_name: courierForm.courier_name || null,
        dispatch_from: courierForm.courier_name || null,
        dispatch_awb: courierForm.dispatch_awb || null,
        tracking_link: courierForm.tracking_link || null,
      };
      const { data } = await axios.put(`${API}/tickets/${id}`, allData, { withCredentials: true });
      setTicket(data); initForms(data);
      setEditAll(false); setEditCustomer(false); setEditCharger(false);
      setEditPayment(false); setEditCourier(false);
      toast.success("All changes saved successfully!");
    } catch { toast.error("Failed to save changes"); }
    finally { setSaving(false); }
  };

  // Form state per section
  const [custForm, setCF] = useState({});
  const [payForm, setPF] = useState({});
  const [courierForm, setCoF] = useState({});
  const [chargerForm, setChargerForm] = useState([]);
  const [repairForm, setRepairForm] = useState({});
  const [xpChargerStatus, setXpChargerStatus] = useState("");

  const initForms = useCallback((d) => {
    setCF({ customer_name:d.customer_name||"", phone:d.phone||"", alternate_phone:d.alternate_phone||"", city:d.city||"", state:d.state||"", pin_code:d.pin_code||"", address:d.address||"" });
    setPF({ payment_total:d.payment_total??d.payment_amount??"", payment_paid:d.payment_paid??"", payment_method:d.payment_method||d.payment_status||"", payment_notes:d.payment_notes||"" });
    setCoF({ courier_name:d.courier_name||d.dispatch_from||"", dispatch_awb:d.dispatch_awb||"", tracking_link:d.tracking_link||"" });
    setChargerForm(normalizeChargers(d));
    setRepairForm({ service_type:d.service_type||"", received_date:d.received_date||"", fault_description:d.fault_description||"", repair_notes:d.repair_notes||"" });
    setXpChargerStatus(d.xp_charger_status || "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // State setters and normalizeChargers are stable references — intentionally omitted

  const reload = useCallback(async () => {
    const { data } = await axios.get(`${API}/tickets/${id}`, { withCredentials: true });
    setTicket(data); initForms(data);
  }, [id, initForms]);

  useEffect(() => {
    axios.get(`${API}/tickets/${id}`, { withCredentials: true })
      .then(r => { setTicket(r.data); initForms(r.data); })
      .catch(() => { toast.error("Ticket not found"); navigate("/tickets"); })
      .finally(() => setLoading(false));
  }, [id, navigate, initForms]);

  const saveSection = async (updates) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/tickets/${id}`, updates, { withCredentials: true });
      setTicket(data); initForms(data);
      toast.success("Saved successfully");
      return data;
    } catch { toast.error("Failed to save"); throw new Error("save failed"); }
    finally { setSaving(false); }
  };

  const handleStatusUpdate = async (newStatus, notes) => {
    const { data } = await axios.patch(`${API}/tickets/${id}/status`, { status: newStatus, notes: notes || null }, { withCredentials: true });
    setTicket(data); initForms(data);
    toast.success(`Status: ${newStatus}`);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this ticket? This cannot be undone.")) return;
    try { await axios.delete(`${API}/tickets/${id}`, { withCredentials: true }); toast.success("Deleted"); navigate("/tickets"); }
    catch { toast.error("Delete failed"); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) { const fd = new FormData(); fd.append("file", f); await axios.post(`${API}/tickets/${id}/upload`, fd, { withCredentials: true }); }
      await reload(); toast.success("Files uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleDeleteImage = async (imgUrl) => {
    const filename = imgUrl.split("/").pop();
    try { await axios.delete(`${API}/tickets/${id}/images?filename=${filename}`, { withCredentials: true }); await reload(); toast.success("Image removed"); }
    catch { toast.error("Failed"); }
  };

  const exportPDF = async () => {
    try {
      const r = await axios.get(`${API}/export/pdf/${id}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `ticket_${ticket.ticket_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF failed"); }
  };

  const saveBarcode = useCallback(() => {
    const svgEl = barcodeRef.current;
    if (!svgEl) return;
    const svgStr = new XMLSerializer().serializeToString(svgEl);
    const blob = new Blob([svgStr], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `barcode_${ticket?.ticket_id}.svg`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Barcode saved!");
  }, [ticket?.ticket_id]);

  const printReceipt = useCallback(() => {
    if (!ticket) return;
    // HTML-escape helper to prevent XSS when writing user data into the print window
    const esc = (s) => String(s ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&#x27;");
    const LOGO_URL = "https://customer-assets-lqy194kg.emergentagent.net/job_charger-service-hub-1/artifacts/1jv6mr6c_2.jpeg";
    const chargers = normalizeChargers(ticket);
    const FRONTEND = window.location.origin;
    const trackUrl = `${FRONTEND}/track/${esc(ticket.ticket_id)}`;
    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(trackUrl)}&color=166534&bgcolor=ffffff&margin=4`;

    // Barcode SVG inline
    const str = (ticket.ticket_id || "").replace(/[^A-Z0-9]/g, "");
    let bx = 0;
    const bars = Array.from({ length: 52 }, (_, i) => {
      const c = str.charCodeAt(i % str.length) || 65;
      const w = 2 + ((c * 3 + i * 7) % 3); const h = 22 + ((c * 11 + i * 13) % 24); const gap = 1 + ((c + i) % 2);
      const r = { x: bx, w, h }; bx += w + gap; return r;
    });
    const barcodeW = Math.min(bx, 200);
    const barcodeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${barcodeW}" height="50" viewBox="0 0 ${bx} 50">
      ${bars.map(r => `<rect x="${r.x}" y="${50 - r.h}" width="${r.w}" height="${r.h}" fill="#111827" rx="0.5"/>`).join("")}
    </svg>`;

    const statusBg = ticket.status === "Completed" ? "#dcfce7" : ticket.status === "Warranty Rejected" ? "#fee2e2" : "#dbeafe";
    const statusTx = ticket.status === "Completed" ? "#166534" : ticket.status === "Warranty Rejected" ? "#991b1b" : "#1e40af";

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>${esc(ticket.ticket_id)} — XSTRONG POWER Service Receipt</title>
    <style>
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:Arial,sans-serif;background:#f3f4f6;padding:16px;color:#111;font-size:12px}
      .card{background:#fff;border:1px solid #e5e7eb;border-radius:12px;margin-bottom:10px;overflow:hidden}
      /* Header */
      .hdr{display:flex;align-items:flex-start;justify-content:space-between;padding:14px 16px;gap:12px}
      .hdr-left{display:flex;align-items:flex-start;gap:10px}.logo{height:52px;object-fit:contain}
      .co-name{color:#16a34a;font-size:15px;font-weight:900;letter-spacing:-0.3px;line-height:1.2}
      .co-sub{font-size:11px;font-weight:700;color:#374151;margin-top:2px}
      .co-info{font-size:10px;color:#6b7280;margin-top:2px;line-height:1.6}
      .hdr-right{text-align:right;flex-shrink:0}
      .ticket-id{font-size:22px;font-weight:900;font-family:monospace;color:#111;letter-spacing:-0.5px}
      .ticket-date{font-size:10px;color:#9ca3af;margin-top:2px}
      .status-badge{display:inline-block;margin-top:4px;padding:3px 10px;border-radius:20px;font-size:10px;font-weight:700;background:${statusBg};color:${statusTx}}
      .green-line{height:3px;background:linear-gradient(to right,#16a34a,#4ade80);margin:0 0 0 0}
      /* Barcode section */
      .barcode-sec{display:flex;align-items:center;gap:14px;padding:12px 16px;border-top:1px solid #f9fafb}
      .barcode-box{background:#f9fafb;border:1px solid #f3f4f6;border-radius:8px;padding:8px;text-align:center}
      .barcode-id{font-family:monospace;font-size:10px;font-weight:700;color:#111;letter-spacing:2px;margin-top:4px}
      .qr-box{text-align:center;flex-shrink:0}
      .scan-info{flex:1}.scan-info p{font-size:12px;font-weight:600;color:#374151}
      .scan-info small{font-size:10px;color:#9ca3af}
      /* Sections */
      .sec-title{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#9ca3af;padding:10px 16px 0}
      .cust-row{display:flex;gap:16px;padding:4px 16px;font-size:12px}
      .cust-lbl{color:#9ca3af;width:64px;flex-shrink:0;font-size:11px}
      .cust-val{font-weight:600;color:#111;flex:1}
      /* Charger table */
      table{width:100%;border-collapse:collapse;font-size:11px}
      thead tr{background:#f9fafb;border-bottom:1px solid #f3f4f6}
      th{padding:8px 12px;text-align:left;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#9ca3af}
      td{padding:10px 12px;border-bottom:1px solid #f9fafb;color:#374151}
      .volt-amp{font-weight:700;color:#16a34a}
      .sn{font-family:monospace;font-size:10px;color:#6b7280}
      /* Fault */
      .fault-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;padding:10px 16px}
      .fault-lbl{font-size:9px;color:#9ca3af;font-weight:700;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px}
      .fault-val{font-size:11px;color:#374151}.fault-na{font-size:11px;color:#d1d5db;font-style:italic}
      /* Courier */
      .courier{background:#eff6ff;border:1px solid #bfdbfe;border-radius:12px;padding:12px 14px;margin-bottom:10px}
      .courier-title{font-size:10px;font-weight:700;color:#1d4ed8;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px}
      .courier-row{display:flex;flex-wrap:wrap;gap:16px;font-size:11px}
      .courier-lbl{color:#6b7280}.courier-val{font-weight:700;color:#111}
      .courier-link{font-size:10px;color:#6b7280;margin-top:4px}
      .courier-link a{color:#2563eb}
      /* History */
      .hist-item{display:flex;align-items:flex-start;gap:8px;padding:6px 0;border-bottom:1px solid #f3f4f6}
      .hist-dot{width:8px;height:8px;border-radius:50%;background:#16a34a;flex-shrink:0;margin-top:3px}
      .hist-status{font-weight:700;font-size:11px}
      .hist-note{font-size:10px;color:#6b7280;margin-top:1px}
      .hist-time{font-size:10px;color:#9ca3af;margin-left:auto;flex-shrink:0;white-space:nowrap}
    </style></head><body>

    <div class="card">
      <!-- Header -->
      <div class="hdr">
        <div class="hdr-left">
          <img src="${LOGO_URL}" class="logo" alt="XP"/>
          <div>
            <div class="co-name">XSTRONG POWER EV CHARGER</div>
            <div class="co-sub">EV Charger Repair & Service Center</div>
            <div class="co-info">
              📞 +91 9871365888 / +91 8800562266<br>
              ✉ xstrongpowerservice@gmail.com<br>
              🕐 Mon-Sat: 10:00 AM – 6:00 PM
            </div>
          </div>
        </div>
        <div class="hdr-right">
          <div class="ticket-id">${esc(ticket.ticket_id)}</div>
          <div class="ticket-date">Date: ${esc(formatDate(ticket.received_date))}</div>
          <div class="status-badge">🏷 ${esc(ticket.status)}</div>
        </div>
      </div>
      <div class="green-line"></div>
      <!-- Barcode + QR -->
      <div class="barcode-sec">
        <div class="barcode-box">
          <div style="font-size:9px;color:#9ca3af;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Shipment Barcode</div>
          ${barcodeSvg}
          <div class="barcode-id">${esc(ticket.ticket_id)}</div>
        </div>
        <div class="qr-box">
          <div style="font-size:9px;color:#9ca3af;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">QR — Customer Track</div>
          <img src="${qrSrc}" width="72" height="72" style="border:1px solid #f3f4f6;border-radius:8px;padding:2px"/>
        </div>
        <div class="scan-info">
          <p>Scan QR to track repair status online</p>
          <small>Attach this barcode to the device/packaging</small>
        </div>
      </div>
    </div>

    <!-- Customer Details -->
    <div class="card">
      <div class="sec-title">Customer Details</div>
      <div style="padding:8px 0 10px">
        <div class="cust-row"><span class="cust-lbl">Name:</span><span class="cust-val">${esc(ticket.customer_name)||"—"}</span></div>
        <div class="cust-row"><span class="cust-lbl">Phone:</span><span class="cust-val">${esc(ticket.phone)||"—"}</span></div>
        ${ticket.alternate_phone?`<div class="cust-row"><span class="cust-lbl">Alt Phone:</span><span class="cust-val">${esc(ticket.alternate_phone)}</span></div>`:""}
        <div class="cust-row"><span class="cust-lbl">Address:</span><span class="cust-val">${esc([ticket.address,ticket.city,ticket.state,ticket.pin_code].filter(Boolean).join(", "))||"—"}</span></div>
      </div>
    </div>

    <!-- Charger Details -->
    ${chargers.length > 0 ? `<div class="card">
      <div class="sec-title">Charger Details (${chargers.length} Unit${chargers.length > 1 ? "s" : ""})</div>
      <table>
        <thead><tr>
          <th style="width:24px">#</th>
          <th>Battery Cut-Off / Ampere</th>
          <th>Serial Number</th>
          <th>Purchase Date</th>
          <th>Body Type</th>
        </tr></thead>
        <tbody>
          ${chargers.map((ch, i) => `<tr>
            <td style="color:#9ca3af">${i + 1}</td>
            <td class="volt-amp">${[ch.voltage,ch.ampere].filter(Boolean).join(" / ")||"—"}</td>
            <td class="sn">${ch.serial_number||"—"}</td>
            <td>${[ch.mfg_month,ch.mfg_year].filter(Boolean).join(" ")||"—"}</td>
            <td>${ch.body_type||"—"}</td>
          </tr>`).join("")}
        </tbody>
      </table>
    </div>` : ""}

    <!-- Fault & Specifications -->
    <div class="card">
      <div class="sec-title">Fault & Specifications</div>
      <div class="fault-grid">
        <div><div class="fault-lbl">Fault Description</div>
          ${ticket.fault_description ? `<div class="fault-val">${ticket.fault_description}</div>` : `<div class="fault-na">Not specified</div>`}
        </div>
        <div><div class="fault-lbl">Technical Notes</div><div class="fault-na">Not specified</div></div>
        <div><div class="fault-lbl">Repair Notes</div><div class="fault-na">Not specified</div></div>
      </div>
    </div>

    <!-- Courier -->
    ${(ticket.courier_name||ticket.dispatch_from||ticket.dispatch_awb||ticket.tracking_link) ? `<div class="courier">
      <div class="courier-title">🚚 Courier / Dispatch Details</div>
      <div class="courier-row">
        ${(ticket.courier_name||ticket.dispatch_from)?`<div><span class="courier-lbl">Courier Service: </span><span class="courier-val">${ticket.courier_name||ticket.dispatch_from}</span></div>`:""}
        ${ticket.dispatch_awb?`<div><span class="courier-lbl">AWB / Tracking No.: </span><span class="courier-val" style="font-family:monospace;color:#1d4ed8">${ticket.dispatch_awb}</span></div>`:""}
      </div>
      ${ticket.tracking_link?`<div class="courier-link">Track at: <a href="${ticket.tracking_link}">${ticket.tracking_link}</a></div>`:""}
    </div>` : ""}

    <!-- Service History -->
    ${(ticket.status_history||[]).length > 0 ? `<div class="card">
      <div class="sec-title">Service History</div>
      <div style="padding:8px 16px 12px">
        ${[...(ticket.status_history||[])].reverse().map(h => `<div class="hist-item">
          <div class="hist-dot"></div>
          <div style="flex:1">
            <div style="display:flex;align-items:baseline;gap:8px">
              <span class="hist-status">${h.status}</span>
              ${h.notes?`<span class="hist-note">${h.notes}</span>`:""}
            </div>
          </div>
          <div class="hist-time">${(h.timestamp||"").slice(0,16).replace("T",", ")}</div>
        </div>`).join("")}
      </div>
    </div>` : ""}

    </body></html>`;
    const w = window.open("about:blank", "_blank", "width=620,height=880");
    if (!w) { toast.error("Popup blocked — please allow popups for this site"); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => { try { w.print(); } catch(err) { console.error("Print dialog error:", err); } }, 500);
  }, [ticket]);

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/track/${ticket.ticket_id}`).catch(() => {});
    setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2500);
    toast.success("Customer tracking link copied!");
  };

  // Notes
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      const { data } = await axios.post(`${API}/tickets/${id}/notes`, { text: newNote.trim() }, { withCredentials: true });
      setTicket(data); initForms(data);
      setNewNote("");
      toast.success("Note added");
    } catch { toast.error("Failed to add note"); }
    finally { setAddingNote(false); }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const { data } = await axios.delete(`${API}/tickets/${id}/notes/${noteId}`, { withCredentials: true });
      setTicket(data); initForms(data);
      toast.success("Note deleted");
    } catch { toast.error("Failed to delete note"); }
  };

  // Charger helpers
  const addCharger = () => setChargerForm(f => [...f, { ...EMPTY_CHARGER }]);
  const removeCharger = (idx) => setChargerForm(f => f.filter((_, i) => i !== idx));
  const setC = (idx, key) => (val) => setChargerForm(f => f.map((c, i) => i === idx ? { ...c, [key]: val } : c));
  const setCE = (idx, key) => (e) => setChargerForm(f => f.map((c, i) => i === idx ? { ...c, [key]: e.target.value } : c));

  if (loading) return (
    <div className="max-w-7xl mx-auto p-6 animate-pulse space-y-4">
      <div className="h-12 bg-gray-200 rounded-2xl" />
      <div className="flex gap-6"><div className="flex-1 h-64 bg-gray-100 rounded-2xl"/><div className="w-80 h-64 bg-gray-100 rounded-2xl"/></div>
    </div>
  );
  if (!ticket) return null;

  const currentIdx = ALL_STATUSES.indexOf(ticket.status);
  const historyMap = {};
  (ticket.status_history || []).forEach(h => { historyMap[h.status] = h; });
  const trackingUrl = `${window.location.origin}/track/${ticket.ticket_id}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(trackingUrl)}&color=166534&bgcolor=ffffff&margin=4`;
  const displayChargers = editCharger ? chargerForm : normalizeChargers(ticket);

  const payTotal = parseFloat(payForm.payment_total !== "" ? payForm.payment_total : (ticket.payment_total ?? ticket.payment_amount ?? 0));
  const payPaid = parseFloat(payForm.payment_paid !== "" ? payForm.payment_paid : (ticket.payment_paid ?? 0));
  const payDue = Math.max(0, payTotal - payPaid);
  const isPaid = payDue === 0 && payTotal > 0;

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* ── TOP BAR ── */}
      <div className="bg-white border-b border-gray-100 px-6 py-3 sticky top-0 z-20 no-print">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => navigate("/tickets")} data-testid="back-btn"
              className="p-1.5 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base font-black text-gray-900 font-mono tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>
              {ticket.ticket_id}
            </h1>
            <StatusBadge status={ticket.status} />
            {/* Color tag dot — clickable */}
            <ColorDot
              color={ticket.color_tag}
              size="lg"
              onColorChange={async (color) => {
                try { await saveSection({ color_tag: color }); }
                catch (err) { console.error("Color save error:", err); }
              }}
            />
            <span className="text-xs text-gray-400 hidden sm:block">Created {formatDate(ticket.received_date)}</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {editAll ? (
              <>
                <button onClick={handleSaveAll} disabled={saving} data-testid="save-all-btn"
                  className="inline-flex items-center gap-1.5 h-8 px-4 rounded-xl bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold transition-colors shadow-sm shadow-lime-900/20 disabled:opacity-50">
                  <Check className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save All"}
                </button>
                <button onClick={handleCancelAll} data-testid="cancel-all-btn"
                  className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
                <button onClick={() => setShowStatusModal(true)} data-testid="update-status-top-btn"
                  className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
                  <RefreshCw className="w-3.5 h-3.5 text-gray-400" /> Update Status
                </button>
              </>
            ) : (
              <>
                <button onClick={copyLink} data-testid="share-link-btn"
                  className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-xl bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold transition-colors shadow-sm shadow-lime-900/20">
                  <Share2 className="w-3.5 h-3.5" /> {linkCopied ? "Copied!" : "Customer Link"}
                </button>
                <button onClick={handleEditAll} data-testid="edit-all-btn"
                  className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
                  <Edit2 className="w-3.5 h-3.5 text-gray-400" /> Edit
                </button>
                <button onClick={() => window.print()} data-testid="print-btn"
                  className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
                  <Printer className="w-3.5 h-3.5 text-gray-400" /> Print
                </button>
                <button onClick={() => setShowStatusModal(true)} data-testid="update-status-top-btn"
                  className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors">
                  <RefreshCw className="w-3.5 h-3.5 text-gray-400" /> Update Status
                </button>
                <button onClick={handleDelete} data-testid="delete-btn"
                  className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-xl border border-red-200 bg-white text-xs font-semibold text-red-500 hover:bg-red-50 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="flex flex-col lg:flex-row gap-5">

          {/* ── LEFT COLUMN ── */}
          <div className="flex-1 min-w-0 space-y-5">

            {/* BARCODE SECTION */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-6 py-3.5 bg-gray-50 border-b border-gray-100">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Shipment Barcode</h3>
              </div>
              <div className="p-6">
                <div className="flex items-stretch gap-5">
                  {/* Barcode */}
                  <div className="flex-1 bg-gray-50 rounded-xl p-4 border border-gray-100 flex flex-col items-center justify-center">
                    <BarcodeStrip ticketId={ticket.ticket_id} ref={barcodeRef} />
                    <p className="text-xs font-mono font-bold text-gray-700 mt-2 tracking-widest">{ticket.ticket_id}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Code-128 Barcode</p>
                  </div>
                  {/* QR */}
                  <div className="flex-shrink-0 flex flex-col items-center justify-center">
                    <div className="p-2 bg-white border border-gray-100 rounded-2xl shadow-sm">
                      <img src={qrUrl} alt="QR Code" className="w-24 h-24" />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1.5 text-center">QR — Scan to Track</p>
                    <a href={trackingUrl} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] text-lime-600 hover:underline font-semibold mt-0.5 text-center">
                      Customer tracking link
                    </a>
                  </div>
                </div>
                <button onClick={saveBarcode} data-testid="save-barcode-btn"
                  className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 bg-lime-600 hover:bg-lime-700 text-white text-sm font-bold rounded-xl transition-colors shadow-sm shadow-lime-900/20">
                  <Download className="w-4 h-4" /> Save Barcode
                </button>
              </div>
            </div>

            {/* CUSTOMER DETAILS */}
            <SectionCard title="Customer Details"
              editMode={editCustomer}
              onEdit={() => setEditCustomer(true)}
              onCancel={() => { setEditCustomer(false); initForms(ticket); }}
              onSave={() => saveSection(custForm).then(() => setEditCustomer(false))}
              saving={saving}>
              <div className="grid grid-cols-2 gap-4">
                {editCustomer ? (
                  <>
                    <EditF label="Customer / Company Name" value={custForm.customer_name} onChange={e => setCF(f => ({...f, customer_name:e.target.value}))} />
                    <EditF label="Phone" value={custForm.phone} onChange={e => setCF(f => ({...f, phone:e.target.value}))} />
                    <EditF label="Alternate Phone" value={custForm.alternate_phone} onChange={e => setCF(f => ({...f, alternate_phone:e.target.value}))} />
                    <EditF label="City" value={custForm.city} onChange={e => setCF(f => ({...f, city:e.target.value}))} />
                    <EditF label="State" value={custForm.state} onChange={e => setCF(f => ({...f, state:e.target.value}))} />
                    <EditF label="PIN Code" value={custForm.pin_code} onChange={e => setCF(f => ({...f, pin_code:e.target.value}))} />
                    <div className="col-span-2"><EditF label="Address" value={custForm.address} onChange={e => setCF(f => ({...f, address:e.target.value}))} multiline /></div>
                  </>
                ) : (
                  <>
                    <InfoRow label="Name" value={ticket.customer_name} />
                    <InfoRow label="Phone" value={ticket.phone} />
                    <InfoRow label="Alt Phone" value={ticket.alternate_phone} />
                    <InfoRow label="City" value={ticket.city} />
                    <InfoRow label="State" value={ticket.state} />
                    <InfoRow label="PIN Code" value={ticket.pin_code} />
                    <InfoRow label="Address" value={ticket.address} full />
                  </>
                )}
              </div>
            </SectionCard>

            {/* CHARGER DETAILS */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-6 py-3.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  Charger Details ({displayChargers.length})
                </h3>
                <div className="flex gap-1.5">
                  {editCharger ? (
                    <>
                      <button onClick={() => { setEditCharger(false); if (!editAll) initForms(ticket); }} className="px-2.5 py-1 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                      <button disabled={saving} onClick={() => saveSection({ chargers: chargerForm, service_type: repairForm.service_type, received_date: repairForm.received_date, fault_description: repairForm.fault_description, repair_notes: repairForm.repair_notes, xp_charger_status: xpChargerStatus }).then(() => setEditCharger(false))}
                        className="px-2.5 py-1 text-xs font-bold text-white bg-lime-600 rounded-lg hover:bg-lime-700 disabled:opacity-50">{saving ? "..." : "Save"}</button>
                    </>
                  ) : (
                    <button onClick={() => setEditCharger(true)} data-testid="edit-charger-btn"
                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <Edit2 className="w-3 h-3" /> Edit
                    </button>
                  )}
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                  {displayChargers.map((charger, idx) => (
                    <div key={idx} className={idx > 0 ? "pt-5 border-t border-gray-100" : ""}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-lime-500 text-white rounded-full flex items-center justify-center text-xs font-black shadow-sm">{idx + 1}</span>
                          <span className="text-sm font-bold text-gray-700">Charger #{idx + 1}</span>
                          {!editCharger && charger.charger_model && <span className="text-xs text-gray-400">— {CHARGER_SEGMENTS[charger.charger_model]?.label || charger.charger_model} · S/N: {charger.serial_number || "—"}</span>}
                        </div>
                        {editCharger && chargerForm.length > 1 && (
                          <button onClick={() => removeCharger(idx)} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-red-50">
                            <X className="w-3.5 h-3.5" /> Remove
                          </button>
                        )}
                      </div>
                      {editCharger ? (
                        <div className="grid grid-cols-2 gap-3">
                          {/* ── Charger type segments ── */}
                          <div className="col-span-2 space-y-2">
                            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block">Charger Type</Label>
                            <div className="flex gap-2">
                              {Object.entries(CHARGER_SEGMENTS).map(([key, seg]) => (
                                <button key={key} type="button"
                                  onClick={() => setChargerForm(f => f.map((c, i) =>
                                    i === idx ? { ...c, charger_model: key, voltage: "", ampere: "" } : c
                                  ))}
                                  className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold border-2 transition-all ${
                                    charger.charger_model === key
                                      ? "bg-lime-50 border-lime-500 text-lime-700"
                                      : "bg-gray-50 border-gray-200 text-gray-500 hover:border-lime-300 hover:text-lime-600"
                                  }`}
                                >
                                  {seg.label}
                                </button>
                              ))}
                            </div>
                            {charger.charger_model && CHARGER_SEGMENTS[charger.charger_model] && (
                              <div>
                                <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5 block">Battery Cut-Off / Ampere</Label>
                                <Select
                                  value={charger.voltage && charger.ampere ? `${charger.voltage}/${charger.ampere}` : "_blank_"}
                                  onValueChange={(val) => {
                                    const { voltage, ampere } = val === "_blank_" ? { voltage:"", ampere:"" } : parseChargerModel(val);
                                    setChargerForm(f => f.map((c, i) => i === idx ? { ...c, voltage, ampere } : c));
                                  }}
                                >
                                  <SelectTrigger className="border-gray-200 focus:ring-lime-500 text-sm">
                                    <SelectValue placeholder="Select voltage / ampere" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="_blank_">— Select voltage / ampere —</SelectItem>
                                    {CHARGER_SEGMENTS[charger.charger_model].models.map((m) => (
                                      <SelectItem key={m} value={m}>{m}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                          <SelF label="Body Type" value={charger.body_type} onChange={setC(idx,"body_type")} options={["Metal body","ALUMINUM body"]} />
                          <EditF label="Serial Number" value={charger.serial_number} onChange={setCE(idx,"serial_number")} />
                          <SelF label="Warranty Status" value={charger.warranty_status} onChange={setC(idx,"warranty_status")} options={["Under warranty","Out of warranty","Open","Damage"]} />
                          <SelF label="Mfg Month" value={charger.mfg_month} onChange={setC(idx,"mfg_month")} options={MONTHS} />
                          <SelF label="Mfg Year" value={charger.mfg_year} onChange={setC(idx,"mfg_year")} options={YEARS} />
                          {/* ── XP Chargers per-charger (edit) ── */}
                          <div className="col-span-2 mt-2 space-y-2">
                            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 block">XP Chargers</Label>
                            {[{v:"returned",l:"Returned to Service Centre"},{v:"new_given",l:"New Given to Customer"}].map(opt => {
                              const sel = charger.xp_charger_status === opt.v;
                              return (
                                <button key={opt.v} type="button"
                                  onClick={() => setChargerForm(f => f.map((c,i) => i===idx ? {...c, xp_charger_status: c.xp_charger_status===opt.v?"":opt.v} : c))}
                                  className={`w-full px-4 py-3 rounded-xl text-sm font-bold text-left flex items-center gap-3 border-2 transition-all ${sel&&opt.v==="returned"?"bg-orange-50 border-orange-500 text-orange-800":sel?"bg-green-50 border-green-500 text-green-800":"bg-gray-50 border-gray-200 text-gray-600"}`}
                                >
                                  <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${sel&&opt.v==="returned"?"border-orange-500 bg-orange-500":sel?"border-green-500 bg-green-500":"border-gray-300 bg-white"}`}>
                                    {sel && <span className="w-2 h-2 bg-white rounded-full" />}
                                  </span>
                                  {opt.l}
                                </button>
                              );
                            })}
                          </div>
                          {/* ── Service Details per-charger (edit) ── */}
                          <SelF label="Service Type" value={charger.service_type} onChange={setC(idx,"service_type")} options={["Repair","Replacement"]} />
                          <div className="col-span-2"><EditF label="Repair Details" value={charger.repair_details} onChange={setCE(idx,"repair_details")} multiline placeholder="Describe the work done by the technician..." /></div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-3">
                            <InfoRow label="Charger Type" value={CHARGER_SEGMENTS[charger.charger_model]?.label || charger.charger_model} />
                            <InfoRow label="Battery Cut-Off / Ampere" value={charger.voltage && charger.ampere ? `${charger.voltage}/${charger.ampere}` : (charger.voltage || charger.ampere || "")} />
                            <InfoRow label="Body Type" value={charger.body_type} />
                            <InfoRow label="Serial Number" value={charger.serial_number} mono />
                            <InfoRow label="Warranty" value={charger.warranty_status} />
                            <InfoRow label="Mfg Month" value={charger.mfg_month} />
                            <InfoRow label="Mfg Year" value={charger.mfg_year} />
                          </div>
                          {/* ── XP Chargers per-charger (view, auto-save) ── */}
                          <div className="pt-3 border-t border-gray-50 space-y-2">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">XP Chargers</p>
                            {[{v:"returned",l:"Returned to Service Centre"},{v:"new_given",l:"New Given to Customer"}].map(opt => {
                              const sel = charger.xp_charger_status === opt.v;
                              return (
                                <button key={opt.v} type="button"
                                  onClick={async () => {
                                    const newV = charger.xp_charger_status === opt.v ? "" : opt.v;
                                    const updated = normalizeChargers(ticket).map((c,i) => i===idx ? {...c, xp_charger_status:newV} : c);
                                    await saveSection({ chargers: updated });
                                  }}
                                  className={`w-full px-4 py-3 rounded-xl text-sm font-bold text-left flex items-center gap-3 border-2 transition-all ${sel&&opt.v==="returned"?"bg-orange-50 border-orange-500 text-orange-800":sel?"bg-green-50 border-green-500 text-green-800":"bg-gray-50 border-gray-200 text-gray-600"}`}
                                >
                                  <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${sel&&opt.v==="returned"?"border-orange-500 bg-orange-500":sel?"border-green-500 bg-green-500":"border-gray-300 bg-white"}`}>
                                    {sel && <span className="w-2 h-2 bg-white rounded-full" />}
                                  </span>
                                  {opt.l}
                                </button>
                              );
                            })}
                          </div>
                          {/* ── Service Details per-charger (view) ── */}
                          {(charger.service_type || charger.repair_details) && (
                            <div className="pt-3 border-t border-gray-50">
                              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">Service Details</p>
                              <div className="space-y-2">
                                {charger.service_type && (
                                  <div>
                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Service Type</p>
                                    <p className="text-sm font-semibold text-gray-900">{charger.service_type}</p>
                                  </div>
                                )}
                                {charger.repair_details && (
                                  <div>
                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-0.5">Repair Details</p>
                                    <p className="text-sm text-gray-700 leading-relaxed">{charger.repair_details}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {editCharger && (
                  <button onClick={addCharger} className="mt-5 w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-sm font-semibold text-gray-500 hover:border-lime-300 hover:text-lime-700 hover:bg-lime-50/50 transition-all">
                    <Plus className="w-4 h-4" /> Add Another Charger
                  </button>
                )}

                {/* ── XP Chargers ── */}
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">XP Chargers</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {/* Returned to Service Centre */}
                    <button
                      type="button"
                      data-testid="xp-returned-btn"
                      onClick={async () => {
                        const v = xpChargerStatus === "returned" ? "" : "returned";
                        setXpChargerStatus(v);
                        await saveSection({ xp_charger_status: v });
                      }}
                      className={`py-3 px-4 rounded-xl text-sm font-bold border-2 text-left flex items-center gap-2.5 transition-all duration-150 ${
                        xpChargerStatus === "returned"
                          ? "bg-orange-50 border-orange-500 text-orange-800"
                          : "bg-gray-50 border-gray-200 text-gray-400 hover:border-orange-300 hover:text-orange-600"
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 transition-all ${
                        xpChargerStatus === "returned"
                          ? "bg-orange-500 border-orange-500"
                          : "border-gray-300"
                      }`} />
                      Returned to Service Centre
                    </button>
                    {/* New Given to Customer */}
                    <button
                      type="button"
                      data-testid="xp-new-given-btn"
                      onClick={async () => {
                        const v = xpChargerStatus === "new_given" ? "" : "new_given";
                        setXpChargerStatus(v);
                        await saveSection({ xp_charger_status: v });
                      }}
                      className={`py-3 px-4 rounded-xl text-sm font-bold border-2 text-left flex items-center gap-2.5 transition-all duration-150 ${
                        xpChargerStatus === "new_given"
                          ? "bg-green-50 border-green-500 text-green-800"
                          : "bg-gray-50 border-gray-200 text-gray-400 hover:border-green-300 hover:text-green-600"
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 transition-all ${
                        xpChargerStatus === "new_given"
                          ? "bg-green-500 border-green-500"
                          : "border-gray-300"
                      }`} />
                      New Given to Customer
                    </button>
                  </div>
                </div>

                {/* Service Details */}
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Service Details</p>
                  </div>
                  {editCharger ? (
                    <div className="grid grid-cols-2 gap-3">
                      <SelF label="Service Type" value={repairForm.service_type} onChange={v => setRepairForm(f => ({...f, service_type:v}))} options={["Repair","Replacement"]} />
                      <EditF label="Received Date" value={repairForm.received_date} onChange={e => setRepairForm(f => ({...f, received_date:e.target.value}))} type="date" />
                      <div className="col-span-2"><EditF label="Fault Description (Customer Complaint)" value={repairForm.fault_description} onChange={e => setRepairForm(f => ({...f, fault_description:e.target.value}))} multiline placeholder="What did the customer report?" /></div>
                      <div className="col-span-2"><EditF label="Repair / Technical Notes" value={repairForm.repair_notes} onChange={e => setRepairForm(f => ({...f, repair_notes:e.target.value}))} multiline /></div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <InfoRow label="Service Type" value={ticket.service_type} />
                      <InfoRow label="Received Date" value={ticket.received_date} />
                      {ticket.fault_description && <InfoRow label="Fault Description" value={ticket.fault_description} full />}
                      {ticket.repair_notes && <InfoRow label="Repair / Technical Notes" value={ticket.repair_notes} full />}
                    </div>
                  )}
                </div>

                {/* Product Images */}
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Product Images</p>
                    <label className="cursor-pointer flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading..." : "Add Files"}
                      <input type="file" multiple accept="image/*,.pdf" onChange={handleUpload} className="hidden" />
                    </label>
                  </div>
                  {(ticket.images||[]).length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">No images uploaded</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {(ticket.images||[]).map((img, i) => {
                        const isPDF = img.toLowerCase().endsWith(".pdf");
                        return (
                          <div key={i} className="relative group rounded-xl overflow-hidden border border-gray-100 aspect-square">
                            {isPDF ? (
                              <div className="w-full h-full bg-red-50 flex items-center justify-center">
                                <a href={`${BACKEND}${img}`} target="_blank" rel="noopener noreferrer" className="text-xs text-red-700 font-bold hover:underline flex flex-col items-center gap-1">
                                  <FileText className="w-5 h-5" /> PDF
                                </a>
                              </div>
                            ) : <img src={`${BACKEND}${img}`} alt="" className="w-full h-full object-cover" />}
                            <button onClick={() => handleDeleteImage(img)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="lg:w-80 flex-shrink-0 space-y-5">

            {/* PAYMENT CARD */}
            <SectionCard title="Payment"
              badge={
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${isPaid ? "bg-lime-50 text-lime-700 border-lime-200" : payTotal > 0 ? "bg-red-50 text-red-600 border-red-200" : "bg-gray-100 text-gray-500 border-gray-200"}`}>
                  {isPaid ? "Paid" : payTotal > 0 ? "Unpaid" : "—"}
                </span>
              }
              editMode={editPayment}
              onEdit={() => setEditPayment(true)}
              onCancel={() => { setEditPayment(false); initForms(ticket); }}
              onSave={() => saveSection({ payment_total: parseFloat(payForm.payment_total)||0, payment_paid: parseFloat(payForm.payment_paid)||0, payment_method: payForm.payment_method, payment_notes: payForm.payment_notes }).then(() => setEditPayment(false))}
              saving={saving}>
              {editPayment ? (
                <div className="space-y-3">
                  <EditF label="Total Amount (₹)" value={String(payForm.payment_total)} onChange={e => setPF(f => ({...f, payment_total:e.target.value}))} type="number" placeholder="0" />
                  <EditF label="Paid Amount (₹)" value={String(payForm.payment_paid)} onChange={e => setPF(f => ({...f, payment_paid:e.target.value}))} type="number" placeholder="0" />
                  <SelF label="Payment Method" value={payForm.payment_method} onChange={v => setPF(f => ({...f, payment_method:v}))} options={["Cash","UPI","Card","Bank Transfer","Cheque","Online"]} />
                  <EditF label="Notes" value={payForm.payment_notes} onChange={e => setPF(f => ({...f, payment_notes:e.target.value}))} multiline />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Total</p>
                      <p className="text-sm font-black text-gray-900">₹{payTotal.toLocaleString("en-IN")}</p>
                    </div>
                    <div className="bg-lime-50 rounded-xl p-3 text-center border border-lime-100">
                      <p className="text-[10px] font-bold text-lime-500 uppercase tracking-wider mb-1">Paid</p>
                      <p className="text-sm font-black text-lime-700">₹{payPaid.toLocaleString("en-IN")}</p>
                    </div>
                    <div className={`rounded-xl p-3 text-center border ${payDue > 0 ? "bg-red-50 border-red-100" : "bg-gray-50 border-gray-100"}`}>
                      <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${payDue > 0 ? "text-red-400" : "text-gray-400"}`}>Due</p>
                      <p className={`text-sm font-black ${payDue > 0 ? "text-red-700" : "text-gray-400"}`}>₹{payDue.toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                  <InfoRow label="Payment Method" value={ticket.payment_method || ticket.payment_status} />
                  {ticket.payment_notes && <div className="mt-2"><InfoRow label="Notes" value={ticket.payment_notes} /></div>}
                </>
              )}
            </SectionCard>

            {/* COURIER DETAILS */}
            <SectionCard title="Courier Details"
              editMode={editCourier}
              onEdit={() => setEditCourier(true)}
              onCancel={() => { setEditCourier(false); initForms(ticket); }}
              onSave={() => saveSection({ courier_name: courierForm.courier_name, dispatch_from: courierForm.courier_name, dispatch_awb: courierForm.dispatch_awb, tracking_link: courierForm.tracking_link }).then(() => setEditCourier(false))}
              saving={saving}>
              {editCourier ? (
                <div className="space-y-3">
                  <EditF label="Courier Name" value={courierForm.courier_name} onChange={e => setCoF(f => ({...f, courier_name:e.target.value}))} placeholder="Delhivery, DTDC, BlueDart..." />
                  <EditF label="AWB Number" value={courierForm.dispatch_awb} onChange={e => setCoF(f => ({...f, dispatch_awb:e.target.value}))} placeholder="AWB / tracking number" />
                  <EditF label="Tracking Link" value={courierForm.tracking_link} onChange={e => setCoF(f => ({...f, tracking_link:e.target.value}))} placeholder="https://..." />
                </div>
              ) : (
                <div className="space-y-3">
                  <InfoRow label="Courier" value={ticket.courier_name || ticket.dispatch_from} />
                  <InfoRow label="AWB Number" value={ticket.dispatch_awb} mono />
                  {ticket.tracking_link && (
                    <a href={ticket.tracking_link} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 transition-colors">
                      <ExternalLink className="w-4 h-4 text-gray-400" /> Track Shipment
                    </a>
                  )}
                </div>
              )}
            </SectionCard>

            {/* QUICK ACTIONS */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3.5 bg-gray-50 border-b border-gray-100">
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Quick Actions</h3>
              </div>
              <div className="p-5 space-y-2">
                <button onClick={() => setShowStatusModal(true)} data-testid="quick-update-status-btn"
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-lime-600 hover:bg-lime-700 text-white text-sm font-bold rounded-xl transition-colors shadow-sm shadow-lime-900/20">
                  <RefreshCw className="w-4 h-4" /> Update Status
                </button>
                <button onClick={exportPDF} data-testid="export-pdf-btn"
                  className="w-full flex items-center justify-center gap-2 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-xl transition-colors">
                  <FileText className="w-4 h-4 text-gray-400" /> Export PDF
                </button>
                <button onClick={printReceipt} data-testid="print-receipt-btn"
                  className="w-full flex items-center justify-center gap-2 py-2.5 border border-gray-200 hover:bg-lime-50 hover:border-lime-200 hover:text-lime-700 text-gray-700 text-sm font-semibold rounded-xl transition-colors">
                  <Printer className="w-4 h-4 text-gray-400" /> Print Receipt
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* ── SERVICE JOURNEY TIMELINE ── */}
        <div className="mt-5 bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-5 pb-3 flex items-center justify-between">
            <h3 className="font-bold text-gray-900 text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Service Journey Timeline
            </h3>
            <span className="text-xs text-gray-400 font-medium">
              {Math.round(((currentIdx + 1) / ALL_STATUSES.length) * 100)}% complete
            </span>
          </div>

          {/* Green progress bar */}
          <div className="px-6 pb-5">
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${ticket.status === "Warranty Rejected" ? "bg-red-500" : "bg-green-500"}`}
                style={{ width: `${Math.round(((currentIdx + 1) / ALL_STATUSES.length) * 100)}%` }}
              />
            </div>
          </div>

          {/* Vertical steps */}
          <div className="px-6 pb-6">
            {ALL_STATUSES.map((status, idx) => {
              const display = STATUS_DISPLAY[status] || { name: status, sub: "", emoji: "📋" };
              const done = idx < currentIdx;
              const current = idx === currentIdx;
              const future = idx > currentIdx;
              const histEntry = historyMap[status];
              const isRejected = status === "Warranty Rejected" && current;

              return (
                <div key={status} className="flex gap-4">
                  {/* Circle + connector line */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all mt-0.5 flex-shrink-0 ${
                      done
                        ? "bg-green-500 border-green-500 shadow-sm shadow-green-200"
                        : current && !isRejected
                        ? "bg-white border-green-500 shadow-md shadow-green-100 ring-4 ring-green-50"
                        : isRejected
                        ? "bg-white border-red-400 shadow-md shadow-red-100 ring-4 ring-red-50"
                        : "bg-white border-gray-200"
                    }`}>
                      {done
                        ? <Check className="w-5 h-5 text-white" strokeWidth={2.5} />
                        : <span className={`text-base leading-none ${future ? "opacity-25" : ""}`}>{display.emoji}</span>
                      }
                    </div>
                    {idx < ALL_STATUSES.length - 1 && (
                      <div className={`w-0.5 h-7 my-1 rounded-full ${done ? "bg-green-300" : "bg-gray-100"}`} />
                    )}
                  </div>

                  {/* Content */}
                  <div className={`pb-6 pt-2 flex-1 min-w-0 ${future ? "opacity-35" : ""}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-bold text-sm leading-tight ${
                        done ? "text-green-800"
                        : current && !isRejected ? "text-gray-900"
                        : isRejected ? "text-red-800"
                        : "text-gray-400"
                      }`}>
                        {display.name}
                      </span>
                      {current && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wide ${
                          isRejected ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                        }`}>
                          Current
                        </span>
                      )}
                      {histEntry && !future && (
                        <span className="text-[10px] text-gray-400 ml-auto whitespace-nowrap">
                          {histEntry.timestamp?.slice(0, 16).replace("T", " ")}
                        </span>
                      )}
                    </div>

                    <p className={`text-xs mt-0.5 leading-relaxed ${
                      done ? "text-green-600" : current ? "text-gray-500" : "text-gray-400"
                    }`}>
                      {display.sub}
                    </p>

                    {current && histEntry?.notes && (
                      <div className="mt-2 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100 flex items-start gap-2">
                        <span className="text-xs flex-shrink-0">📝</span>
                        <span className="text-xs text-gray-600">{histEntry.notes}</span>
                      </div>
                    )}
                    {done && histEntry?.notes && (
                      <p className="text-[11px] text-green-600 mt-0.5 italic">"{histEntry.notes}"</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      {/* ── INTERNAL NOTES ── */}
      <div className="mt-5 bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
        <div className="px-6 py-3.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Internal Notes</h3>
          {(ticket.notes || []).length > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full border border-blue-200">
              {ticket.notes.length}
            </span>
          )}
          <span className="text-[10px] text-gray-400 ml-auto italic">Internal only — not visible to customers</span>
        </div>
        <div className="p-5">
          {/* Add note */}
          <div className="flex gap-2 mb-4">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && e.ctrlKey) handleAddNote(); }}
              placeholder="Type an internal note... (Ctrl+Enter to save)"
              rows={2}
              data-testid="note-input"
              className="flex-1 text-sm border-gray-200 focus:ring-lime-500 focus:border-lime-500 resize-none"
            />
            <button
              onClick={handleAddNote}
              disabled={!newNote.trim() || addingNote}
              data-testid="add-note-btn"
              className="self-end px-4 py-2 bg-lime-600 hover:bg-lime-700 text-white text-sm font-bold rounded-xl disabled:opacity-40 transition-colors shadow-sm"
            >
              {addingNote ? "..." : "Save"}
            </button>
          </div>
          {/* Notes list — newest first */}
          {(ticket.notes || []).length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4 italic">No notes yet</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {[...(ticket.notes || [])].reverse().map((note) => (
                <div key={note.id} className="bg-amber-50 border border-amber-100 rounded-xl p-3.5 group">
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{note.text}</p>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-amber-100">
                    <p className="text-[10px] text-gray-400 font-medium">
                      {note.created_at ? new Date(note.created_at).toLocaleString("en-IN", {
                        day: "numeric", month: "short", year: "numeric",
                        hour: "2-digit", minute: "2-digit"
                      }) : ""}
                    </p>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className="text-[10px] text-red-400 hover:text-red-600 font-bold opacity-0 group-hover:opacity-100 transition-all"
                      data-testid={`delete-note-${note.id}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      </div>

      {/* STATUS MODAL */}
      {showStatusModal && (
        <StatusModal ticket={ticket} onUpdate={handleStatusUpdate} onClose={() => setShowStatusModal(false)} />
      )}

    </div>
  );
}
